/**
 * A partner's proposal, read inside the portal.
 *
 * The dashboard used to LINK to /proposal/:token, which navigates out of the portal to a page
 * with no navigation on it. A partner who accepted their terms was then stranded on a
 * standalone document with no way back — at the exact moment they had just said yes and most
 * wanted to get on with it.
 *
 * So a signed-in partner reads it here and never leaves. The public route still exists and is
 * untouched: it is the link that arrives by email, and it has to work with no session at all.
 * Both render the same ProposalContent.
 *
 * Two differences from the page, both because this reader is not evaluating an offer cold:
 *   - Accept is at the TOP. They have read this before; making them scroll a full rate card
 *     to say yes is a tax on the one action the document exists for.
 *   - It closes on accept. The confirmation is a toast and the dashboard behind it refreshes,
 *     which is where they were going anyway.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ProposalContent, ProposalLoading } from './ProposalContent';

const ProposalDialog = ({ token, open, onOpenChange, onAccepted }) => {
  const { API_URL } = useAuth();
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState('');
  const [deciding, setDeciding] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/proposals/${token}`);
      setProposal(res.data);
    } catch {
      setError('Could not load your proposal. Try the link we emailed you, or ask your contact.');
    }
  }, [token, API_URL]);

  // Re-read on every open rather than once: an admin can send a revised proposal while the
  // partner has the dashboard sitting open, and a cached document would be the wrong offer.
  useEffect(() => { if (open) { setProposal(null); load(); } }, [open, load]);

  const accept = async () => {
    setDeciding(true);
    try {
      await axios.post(`${API_URL}/api/proposals/${token}/accept`);
      toast.success('Accepted — we will be in touch to get you set up.');
      onOpenChange(false);
      onAccepted?.();
    } catch (e) {
      // Left OPEN on failure. Closing on an error would look exactly like closing on success.
      toast.error(e.response?.data?.detail || 'Could not record that. Please try again.');
    } finally {
      setDeciding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* max-h + overflow, or the bottom of a full rate card is unreachable — a Radix dialog
          with no height cap runs off the screen. Same pattern as the other long dialogs. */}
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Your proposal
            {proposal?.reference ? <span className="ml-2 text-sm font-normal text-[#64748B]">
              {proposal.reference}</span> : null}
          </DialogTitle>
        </DialogHeader>

        {error && <p className="text-sm text-[#64748B] py-8 text-center">{error}</p>}
        {!error && !proposal && <ProposalLoading />}
        {!error && proposal && (
          <ProposalContent proposal={proposal} onAccept={accept} deciding={deciding} acceptAtTop />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProposalDialog;
